import React, { useEffect, useRef, useState, useMemo } from "react";
import classNames from "classnames";
import styles from "./styles.module.scss";
import { useThemeProvider } from "@contexts/themeContext";
import dayjs from "dayjs";

const Navigator = ({ active, setActive, assignments }) => {
  const { theme, direction } = useThemeProvider();
  const containerRef = useRef(null);
  const [closestDate, setClosestDate] = useState(null);

  // Memoize and clean up match dates
  const uniqueDates = useMemo(() => {
    return assignments
      .map((assignment) => {
        const rawDate = assignment.details?.date;
        const gameday = assignment.details?.gameday || "N/A";
        if (!rawDate) return null;

        const isoDate = dayjs(rawDate).format("YYYY-MM-DD");
        return { isoDate, gameday };
      })
      .filter(Boolean)
      .filter(
        (value, index, self) =>
          self.findIndex((v) => v.isoDate === value.isoDate) === index
      )
      .sort((a, b) => dayjs(a.isoDate).unix() - dayjs(b.isoDate).unix());
  }, [assignments]);

  // Auto-select closest upcoming date
  useEffect(() => {
    if (!closestDate && uniqueDates.length > 0) {
      const today = dayjs();
      const upcoming = uniqueDates.find(
        ({ isoDate }) =>
          dayjs(isoDate).isAfter(today) || dayjs(isoDate).isSame(today, "day")
      );
      if (upcoming && active !== upcoming.isoDate) {
        setClosestDate(upcoming.isoDate);
        setActive(upcoming.isoDate);
      }
    }
  }, [uniqueDates, closestDate, active, setActive]);

  // Scroll to selected item
  useEffect(() => {
    const container = containerRef.current;
    const activeElement = container?.querySelector(`.${styles.active}`);
    if (activeElement && container) {
      activeElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [active]);

  return (
    <div
      ref={containerRef}
      className={classNames(styles.navigator, {
        [styles.light]: theme === "light",
        [styles.dark]: theme !== "light",
      })}
      style={{
        overflowX: "auto",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        gap: "30px",
        padding: "10px",
        scrollSnapType: "x mandatory",
      }}
    >
      {uniqueDates.map(({ isoDate, gameday }) => (
        <div
          key={isoDate}
          onClick={() => setActive(isoDate)}
          className={classNames(
            styles.slide,
            styles.navigator_item,
            styles[direction],
            {
              [styles.active]: active === isoDate,
            }
          )}
          style={{
            scrollSnapAlign: "center",
            padding: "10px",
            cursor: "pointer",
            textAlign: "center",
            backgroundColor: active === isoDate ? "black" : "transparent",
            color: active === isoDate ? "white" : "inherit",
          }}
        >
          <h4 className={styles.day}>{dayjs(isoDate).format("D.M")}</h4>
          <span className="label h6">{`Spieltag ${gameday}`}</span>
        </div>
      ))}
    </div>
  );
};

export default Navigator;
